import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Button, Badge, Modal, ProgressBar, OverlayTrigger, Tooltip, Form } from 'react-bootstrap';
import { colors, gradients } from '../../brand-colors';

// ---------------------------------------------------------------------------
// Section ordering & grouping for table of contents / rendering
// ---------------------------------------------------------------------------
const SECTION_GROUPS = [
  {
    group: 'Brand Foundation',
    sections: [
      { key: 'direct_archetypes', title: 'Brand Archetypes', direct: true },
      { key: 'brand_promise', title: 'Brand Promise' },
      { key: 'positioning_statement', title: 'Positioning Statement' },
      { key: 'archetype_rationale', title: 'Archetype Rationale' },
    ],
  },
  {
    group: 'Voice & Tone',
    sections: [
      { key: 'direct_voice', title: 'Voice Description', direct: true },
      { key: 'voice_attributes', title: 'Voice Attributes' },
      { key: 'tone_spectrum', title: 'Tone Spectrum' },
      { key: 'writing_dos_donts', title: 'Writing Dos & Don\'ts' },
      { key: 'sample_copy_blocks', title: 'Sample Copy Blocks' },
    ],
  },
  {
    group: 'Audience & Messaging',
    sections: [
      { key: 'audience_language_map', title: 'Audience Language Map' },
      { key: 'decision_drivers', title: 'Decision Drivers' },
      { key: 'competitive_position', title: 'Competitive Positioning' },
      { key: 'messaging_hierarchy', title: 'Messaging Hierarchy' },
    ],
  },
  {
    group: 'Visual Identity',
    sections: [
      { key: 'direct_colors', title: 'Color Palette', direct: true },
      { key: 'direct_typography', title: 'Typography', direct: true },
      { key: 'direct_logo', title: 'Logo', direct: true },
      { key: 'direct_iconography', title: 'Brand Marker', direct: true },
      { key: 'direct_style', title: 'Style & Texture', direct: true },
      { key: 'direct_mood', title: 'Mood Board', direct: true },
      { key: 'semantic_colors', title: 'Semantic Color System' },
      { key: 'dark_mode_colors', title: 'Dark Mode Palette' },
      { key: 'contrast_ratios', title: 'Contrast Ratios (WCAG)' },
      { key: 'imagery_guidelines', title: 'Imagery Guidelines' },
      { key: 'ai_image_prompts', title: 'AI Image Prompt Templates' },
    ],
  },
  {
    group: 'Design System',
    sections: [
      { key: 'type_scale', title: 'Typography Scale' },
      { key: 'spacing_system', title: 'Spacing System' },
      { key: 'layout_grid', title: 'Layout Grid' },
    ],
  },
  {
    group: 'Application Guidelines',
    sections: [
      { key: 'application_website', title: 'Website' },
      { key: 'application_email', title: 'Email' },
      { key: 'application_social', title: 'Social Media' },
      { key: 'application_sales', title: 'Sales Pages' },
    ],
  },
  {
    group: 'Reference',
    sections: [
      { key: 'brand_checklist', title: 'Brand Consistency Checklist' },
    ],
  },
];

function BrandGuide() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [context, setContext] = useState(null);
  const [sections, setSections] = useState({});
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState({ current: '', done: [], errors: [] });
  const [showToc, setShowToc] = useState(true);
  const [activeSection, setActiveSection] = useState('');
  const [editingSection, setEditingSection] = useState(null);
  const [editContent, setEditContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regeneratingKey, setRegeneratingKey] = useState(null);
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [regenSelections, setRegenSelections] = useState({});
  const [brandHashtags, setBrandHashtags] = useState([]);
  const [hashtagGenerating, setHashtagGenerating] = useState(false);
  const [editingHashtags, setEditingHashtags] = useState(false);
  const [editHashtagData, setEditHashtagData] = useState([]);
  const sectionRefs = useRef({});

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------
  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ctxRes, secRes, htRes] = await Promise.all([
        fetch('/api/brand-guide/context', { credentials: 'include' }),
        fetch('/api/brand-guide/sections', { credentials: 'include' }),
        fetch('/api/business/field/brand_hashtags', { credentials: 'include' }),
      ]);
      if (ctxRes.ok) setContext(await ctxRes.json());
      else setError('Failed to load brand context');
      if (secRes.ok) setSections(await secRes.json());
      if (htRes.ok) {
        const htData = await htRes.json();
        if (htData.field_value) {
          try { setBrandHashtags(JSON.parse(htData.field_value)); } catch (e) {}
        }
      }
    } catch (err) {
      setError('Error loading data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Brand Hashtag Generation & Editing
  // ---------------------------------------------------------------------------
  const handleGenerateHashtags = async () => {
    setHashtagGenerating(true);
    try {
      const response = await fetch('/api/brand-guide/brand-hashtags/generate', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success && data.brand_hashtags) {
        setBrandHashtags(data.brand_hashtags);
      }
    } catch (err) {
      console.error('Error generating hashtags:', err);
    } finally {
      setHashtagGenerating(false);
    }
  };

  const handleSaveHashtags = async () => {
    try {
      const response = await fetch('/api/brand-guide/brand-hashtags', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ brand_hashtags: editHashtagData }),
      });
      const data = await response.json();
      if (data.success) {
        setBrandHashtags(editHashtagData);
        setEditingHashtags(false);
      }
    } catch (err) {
      console.error('Error saving hashtags:', err);
    }
  };

  const startEditHashtags = () => {
    setEditHashtagData(brandHashtags.length > 0
      ? brandHashtags.map(h => ({ ...h }))
      : [{ tag: '#', description: '' }, { tag: '#', description: '' }, { tag: '#', description: '' }]
    );
    setEditingHashtags(true);
  };

  // ---------------------------------------------------------------------------
  // AI Generation
  // ---------------------------------------------------------------------------
  const AI_KEYS = [
    'brand_promise','positioning_statement','archetype_rationale',
    'voice_attributes','tone_spectrum','writing_dos_donts','sample_copy_blocks',
    'audience_language_map','decision_drivers','competitive_position',
    'messaging_hierarchy','semantic_colors','dark_mode_colors',
    'imagery_guidelines','ai_image_prompts',
    'application_website','application_email','application_social','application_sales',
    'brand_checklist',
  ];

  const handleGenerate = () => {
    const hasSections = Object.keys(sections).length > 0;
    if (!hasSections) {
      // First-time generation — go directly
      executeRegeneration([]);
      return;
    }

    const editedKeys = Object.entries(sections)
      .filter(([, s]) => s.user_edited)
      .map(([k]) => k);

    if (editedKeys.length === 0) {
      // No edited sections — simple confirm
      if (!window.confirm('This will regenerate ALL sections. Any existing content will be replaced.\n\nContinue?')) return;
      executeRegeneration([]);
    } else {
      // Edited sections exist — show modal with checkboxes (unchecked = preserve by default)
      const defaults = {};
      editedKeys.forEach(k => { defaults[k] = false; });
      setRegenSelections(defaults);
      setShowRegenModal(true);
    }
  };

  const executeRegeneration = async (excludeKeys) => {
    setShowRegenModal(false);
    setGenerating(true);
    setGenProgress({ current: '', done: [], errors: [] });

    try {
      const response = await fetch('/api/brand-guide/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ exclude_keys: excludeKeys }),
      });

      if (response.ok) {
        const data = await response.json();
        // Merge results into sections state (preserved sections remain untouched)
        const updated = { ...sections };
        for (const [key, val] of Object.entries(data.results || {})) {
          updated[key] = {
            title: val.title,
            content: val.content,
            is_ai_generated: true,
            user_edited: false,
            generated_at: new Date().toISOString(),
          };
        }
        setSections(updated);
        setGenProgress({
          current: '',
          done: Object.keys(data.results || {}),
          errors: Object.entries(data.errors || {}).map(([k, v]) => `${k}: ${v}`),
        });
        // Update brand hashtags if they were generated
        if (data.results?.brand_hashtags?.content) {
          setBrandHashtags(data.results.brand_hashtags.content);
        }
        // Re-fetch context so stale flag updates
        const ctxRes = await fetch('/api/brand-guide/context', { credentials: 'include' });
        if (ctxRes.ok) setContext(await ctxRes.json());
      } else {
        const err = await response.json();
        setError(err.error || 'Generation failed');
      }
    } catch (err) {
      setError('Generation error: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateSection = async (sectionKey) => {
    // If user-edited, confirm
    const sec = sections[sectionKey];
    if (sec?.user_edited) {
      if (!window.confirm('Your edits will be lost. Regenerate this section?')) return;
    }
    setRegeneratingKey(sectionKey);
    try {
      const response = await fetch('/api/brand-guide/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ section_key: sectionKey }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.results?.[sectionKey]) {
          setSections(prev => ({
            ...prev,
            [sectionKey]: {
              title: data.results[sectionKey].title,
              content: data.results[sectionKey].content,
              is_ai_generated: true,
              user_edited: false,
              generated_at: new Date().toISOString(),
            },
          }));
        }
        if (data.errors?.[sectionKey]) {
          setError(`Regeneration error: ${data.errors[sectionKey]}`);
        }
      }
    } catch (err) {
      setError('Regeneration error: ' + err.message);
    } finally {
      setRegeneratingKey(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Editing
  // ---------------------------------------------------------------------------
  const handleStartEdit = (sectionKey) => {
    setEditingSection(sectionKey);
    setEditContent(JSON.parse(JSON.stringify(sections[sectionKey]?.content || {})));
  };

  const handleSaveEdit = async () => {
    if (!editingSection) return;
    setSaving(true);
    try {
      const response = await fetch(`/api/brand-guide/sections/${editingSection}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: editContent }),
      });
      if (response.ok) {
        const data = await response.json();
        setSections(prev => ({
          ...prev,
          [editingSection]: {
            ...prev[editingSection],
            content: data.content,
            user_edited: true,
            updated_at: data.updated_at,
          },
        }));
        setEditingSection(null);
        setEditContent(null);
      } else {
        const err = await response.json();
        setError(err.error || 'Save failed');
      }
    } catch (err) {
      setError('Save error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Exports
  // ---------------------------------------------------------------------------
  const handlePrint = () => window.print();

  const handleDownloadTokens = async () => {
    try {
      const res = await fetch('/api/brand-guide/design-tokens', { credentials: 'include' });
      if (res.ok) {
        const tokens = await res.json();
        const blob = new Blob([JSON.stringify(tokens, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const bizName = (context?.business?.business_name || 'brand').replace(/\s+/g, '_').toLowerCase();
        a.href = url;
        a.download = `${bizName}_design_tokens.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError('Download error: ' + err.message);
    }
  };

  const handleDownloadElementor = async () => {
    try {
      const res = await fetch('/api/brand-guide/elementor-palette', { credentials: 'include' });
      if (res.ok) {
        const palette = await res.json();
        const blob = new Blob([JSON.stringify(palette, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const bizName = (context?.business?.business_name || 'brand').replace(/\s+/g, '_').toLowerCase();
        a.href = url;
        a.download = `${bizName}_elementor_colors.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const err = await res.json();
        setError(err.error || 'Export failed');
      }
    } catch (err) {
      setError('Export error: ' + err.message);
    }
  };

  // ---------------------------------------------------------------------------
  // Copy to clipboard
  // ---------------------------------------------------------------------------
  const copyText = (text) => {
    navigator.clipboard.writeText(text);
  };

  // ---------------------------------------------------------------------------
  // Scroll / TOC
  // ---------------------------------------------------------------------------
  const scrollToSection = (key) => {
    const el = sectionRefs.current[key];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(key);
    }
  };

  // ---------------------------------------------------------------------------
  // Stale data detection
  // ---------------------------------------------------------------------------
  const isStale = () => context?.stale === true;

  // ---------------------------------------------------------------------------
  // Loading / empty states
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading your brand guide...</p>
      </Container>
    );
  }

  const hasAnyBrandData = context && (
    context.design?.custom_colors ||
    context.design?.selected_fonts ||
    context.archetypes?.primary ||
    context.design?.brand_voice_description
  );

  if (!hasAnyBrandData) {
    return (
      <Container className="py-4">
        <h1 className="mb-4" style={{ fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif", letterSpacing: '0.15em' }}>
          Full Brand Guide
        </h1>
        <Alert variant="warning" style={{ backgroundColor: '#fff8e6', borderColor: colors.goldMid }}>
          <Alert.Heading>No Brand Identity Found</Alert.Heading>
          <p>
            Your full brand guide will appear here once you complete the Brand Identity section in your Profit Map.
            This includes your archetypes, colors, fonts, logo direction, and brand voice.
          </p>
          <Button
            variant="dark"
            onClick={() => window.location.href = '/profit-map'}
            style={{ backgroundColor: colors.greyDarkest }}
          >
            Go to Profit Map
          </Button>
        </Alert>
      </Container>
    );
  }

  const hasSections = Object.keys(sections).length > 0;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="brand-guide-wrapper">
      {/* Cover page - visible only in print */}
      <div className="print-only-cover no-screen">
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif", letterSpacing: '0.15em', fontSize: '2.5rem' }}>
            {context?.business?.business_name || 'Brand Guide'}
          </h1>
          <hr style={{ width: '200px', margin: '24px auto', borderColor: colors.goldMid }} />
          <h2 style={{ fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif", letterSpacing: '0.1em', color: colors.greyMid, fontSize: '1.5rem' }}>
            Brand Guide
          </h2>
          <p style={{ color: colors.greyMid, marginTop: '48px' }}>
            Generated {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      <Container fluid className="py-4 px-4 no-print-cover">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-start mb-4 no-print">
          <div>
            <h1 style={{ fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif", letterSpacing: '0.15em' }}>
              Full Brand Guide
            </h1>
            <p className="text-muted mb-0">
              {context?.business?.business_name && <span>{context.business.business_name} — </span>}
              Comprehensive brand identity reference
            </p>
          </div>
          {hasSections && (
            <div className="d-flex gap-2 flex-wrap">
              <Button variant="outline-dark" size="sm" onClick={handlePrint}>
                Print / Save PDF
              </Button>
              <Button variant="outline-dark" size="sm" onClick={handleDownloadTokens}>
                Design Tokens
              </Button>
              <Button variant="outline-dark" size="sm" onClick={handleDownloadElementor}>
                Elementor Colors
              </Button>
            </div>
          )}
        </div>

        {error && <Alert variant="danger" dismissible onClose={() => setError('')} className="no-print">{error}</Alert>}

        {isStale() && (
          <Alert variant="warning" className="no-print" style={{ backgroundColor: '#fff8e6', borderColor: colors.goldMid }}>
            <strong>Brand data has changed.</strong>{' '}
            {context.stale_reason || 'Brand identity was updated since this guide was generated.'}{' '}
            <Button variant="link" className="p-0" onClick={handleGenerate} style={{ color: colors.goldDark, fontWeight: 600 }}>
              Regenerate
            </Button>
          </Alert>
        )}

        {/* Generation progress */}
        {generating && (
          <Alert variant="info" className="no-print" style={{ backgroundColor: '#eef3f8', borderColor: colors.navy }}>
            <div className="mb-2"><strong>Generating brand guide sections...</strong> This may take a few minutes.</div>
            <Spinner animation="border" size="sm" style={{ color: colors.goldMid }} className="me-2" />
            <span>Processing all sections...</span>
          </Alert>
        )}

        {genProgress.errors.length > 0 && !generating && (
          <Alert variant="warning" className="no-print">
            <strong>Some sections had errors:</strong>
            <ul className="mb-0 mt-1">
              {genProgress.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </Alert>
        )}

        <Row>
          {/* Table of Contents sidebar */}
          <Col lg={3} className="no-print">
            <div style={{ position: 'sticky', top: '80px' }}>
              <Card className="shadow-sm mb-3">
                <Card.Header
                  style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, cursor: 'pointer' }}
                  onClick={() => setShowToc(!showToc)}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Contents</h6>
                    <span>{showToc ? '−' : '+'}</span>
                  </div>
                </Card.Header>
                {showToc && (
                  <Card.Body style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', padding: '8px 0' }}>
                    {SECTION_GROUPS.map((grp) => (
                      <div key={grp.group} className="mb-2">
                        <div style={{ padding: '4px 16px', fontWeight: 600, fontSize: '0.8rem', color: colors.greyMid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {grp.group}
                        </div>
                        {grp.sections.map((sec) => {
                          const hasContent = sec.direct || sections[sec.key];
                          return (
                            <div
                              key={sec.key}
                              onClick={() => scrollToSection(sec.key)}
                              style={{
                                padding: '4px 16px 4px 24px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                color: activeSection === sec.key ? colors.goldDark : hasContent ? colors.greyDarkest : colors.greyLight,
                                borderLeft: activeSection === sec.key ? `3px solid ${colors.goldLight}` : '3px solid transparent',
                                backgroundColor: activeSection === sec.key ? '#f8f4ed' : 'transparent',
                              }}
                            >
                              {sec.title}
                              {sections[sec.key]?.user_edited && (
                                <Badge bg="warning" text="dark" style={{ fontSize: '0.6rem', marginLeft: 6 }}>edited</Badge>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </Card.Body>
                )}
              </Card>
            </div>
          </Col>

          {/* Main content */}
          <Col lg={9} className="pdf-main-content">
            {/* Brand Hashtags Callout */}
            <div className="mb-4 print-section no-print-if-empty" id="brand-hashtags" style={{
              border: `2px solid ${colors.goldMid}`,
              borderRadius: '12px',
              padding: '1.5rem',
              background: `linear-gradient(135deg, #fffdf5 0%, #fff9e6 100%)`,
            }}>
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h4 style={{
                    fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
                    letterSpacing: '0.1em',
                    marginBottom: '0.25rem',
                  }}>
                    Your Brand Hashtags
                  </h4>
                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                    Proprietary content anchors — every piece of content aligns with one of these, building thematic consistency across all platforms.
                  </p>
                </div>
                <div className="d-flex gap-2 no-print" style={{ flexShrink: 0 }}>
                  {!editingHashtags && (
                    <>
                      <Button
                        size="sm"
                        variant="outline-dark"
                        onClick={startEditHashtags}
                      >
                        {brandHashtags.length > 0 ? 'Edit' : 'Add Manually'}
                      </Button>
                      <Button
                        size="sm"
                        variant="dark"
                        style={{ backgroundColor: colors.greyDarkest }}
                        onClick={handleGenerateHashtags}
                        disabled={hashtagGenerating}
                      >
                        {hashtagGenerating ? <><Spinner animation="border" size="sm" className="me-1" /> Generating...</> : brandHashtags.length > 0 ? 'Regenerate' : 'Generate with AI'}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {editingHashtags ? (
                <div className="mt-3">
                  {editHashtagData.map((ht, i) => (
                    <div key={i} className="d-flex gap-2 mb-2 align-items-start">
                      <input
                        type="text"
                        className="form-control"
                        style={{ maxWidth: '220px' }}
                        placeholder="#CamelCaseTag"
                        value={ht.tag}
                        onChange={(e) => {
                          const updated = [...editHashtagData];
                          updated[i] = { ...updated[i], tag: e.target.value };
                          setEditHashtagData(updated);
                        }}
                      />
                      <input
                        type="text"
                        className="form-control"
                        placeholder="What topics this hashtag covers..."
                        value={ht.description}
                        onChange={(e) => {
                          const updated = [...editHashtagData];
                          updated[i] = { ...updated[i], description: e.target.value };
                          setEditHashtagData(updated);
                        }}
                      />
                    </div>
                  ))}
                  <div className="d-flex gap-2 mt-2">
                    <Button size="sm" variant="dark" style={{ backgroundColor: colors.greyDarkest }} onClick={handleSaveHashtags}>
                      Save Hashtags
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => setEditingHashtags(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : brandHashtags.length > 0 ? (
                <div className="d-flex flex-wrap gap-3 mt-3">
                  {brandHashtags.map((ht, i) => (
                    <div key={i} style={{
                      flex: '1 1 250px',
                      padding: '1rem',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: `1px solid ${colors.goldMid}`,
                    }}>
                      <div style={{
                        fontSize: '1.2rem',
                        fontWeight: 700,
                        color: colors.greyDarkest,
                        marginBottom: '0.25rem',
                      }}>
                        {ht.tag}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: colors.greyMid }}>
                        {ht.description}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted mt-3 mb-0">
                  No brand hashtags yet. Click <strong>Generate with AI</strong> to create them, or <strong>Add Manually</strong> if you already know yours.
                </p>
              )}
            </div>

            {SECTION_GROUPS.map((grp, grpIdx) => (
              <div key={grp.group} className="mb-4 print-section">
                <div className="d-flex justify-content-between align-items-center mb-3 print-group-header" style={{
                  borderBottom: `2px solid ${colors.goldMid}`,
                  paddingBottom: '8px',
                }}>
                  <h3 className="mb-0" style={{
                    fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
                    letterSpacing: '0.1em',
                  }}>
                    {grp.group}
                  </h3>
                  {grpIdx === 0 && (
                    <Button
                      disabled={generating}
                      onClick={handleGenerate}
                      className="no-print"
                      style={{
                        background: hasSections ? colors.greyDarkest : gradients.redButton,
                        border: 'none',
                        color: hasSections ? colors.goldLight : '#fff',
                        padding: '10px 24px',
                        fontSize: '1rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {generating ? (
                        <><Spinner animation="border" size="sm" className="me-1" /> Generating...</>
                      ) : hasSections ? 'Regenerate' : 'Generate Full Brand Guide'}
                    </Button>
                  )}
                </div>

                {grp.sections.map((sec) => (
                  <div
                    key={sec.key}
                    ref={(el) => { sectionRefs.current[sec.key] = el; }}
                    className="mb-4 print-card-section"
                  >
                    {sec.direct ? (
                      renderDirectSection(sec.key, context)
                    ) : sections[sec.key] ? (
                      <Card className="shadow-sm">
                        <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">{sec.title}</h5>
                            <div className="d-flex gap-1 no-print">
                              {sections[sec.key]?.user_edited && (
                                <Badge bg="warning" text="dark" style={{ fontSize: '0.7rem' }}>edited</Badge>
                              )}
                              {sections[sec.key]?.is_ai_generated && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  onClick={() => handleRegenerateSection(sec.key)}
                                  disabled={regeneratingKey === sec.key}
                                  style={{ color: colors.goldLight, padding: '0 4px', fontSize: '0.8rem' }}
                                >
                                  {regeneratingKey === sec.key ? <Spinner animation="border" size="sm" /> : 'Regenerate'}
                                </Button>
                              )}
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => handleStartEdit(sec.key)}
                                style={{ color: colors.goldLight, padding: '0 4px', fontSize: '0.8rem' }}
                              >
                                Edit
                              </Button>
                            </div>
                          </div>
                        </Card.Header>
                        <Card.Body>
                          {renderSectionContent(sec.key, sections[sec.key].content)}
                        </Card.Body>
                      </Card>
                    ) : (
                      <Card className="shadow-sm" style={{ opacity: 0.6 }}>
                        <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                          <h5 className="mb-0">{sec.title}</h5>
                        </Card.Header>
                        <Card.Body className="text-center py-4">
                          <p className="text-muted mb-2">Not yet generated.</p>
                          <div className="d-flex gap-2 justify-content-center no-print">
                            <Button
                              size="sm"
                              disabled={regeneratingKey === sec.key}
                              onClick={() => handleRegenerateSection(sec.key)}
                              style={{ backgroundColor: colors.greyDarkest, borderColor: colors.greyDarkest, color: colors.goldLight }}
                            >
                              {regeneratingKey === sec.key
                                ? <><Spinner animation="border" size="sm" className="me-1" /> Generating...</>
                                : `Generate ${sec.title}`}
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </Col>
        </Row>
      </Container>

      {/* Edit Modal */}
      <Modal show={!!editingSection} onHide={() => { setEditingSection(null); setEditContent(null); }} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>Edit: {sections[editingSection]?.title || editingSection}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editContent && (
            <EditForm
              content={editContent}
              onChange={setEditContent}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => { setEditingSection(null); setEditContent(null); }}>Cancel</Button>
          <Button
            onClick={handleSaveEdit}
            disabled={saving}
            style={{ backgroundColor: colors.greyDarkest, borderColor: colors.greyDarkest, color: colors.goldLight }}
          >
            {saving ? <Spinner animation="border" size="sm" /> : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Regeneration Modal — shows edited sections as checkboxes */}
      <Modal show={showRegenModal} onHide={() => setShowRegenModal(false)} size="md">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>Regenerate Brand Guide</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            The following sections have been manually edited. By default, your edits will be preserved.
            Check any you want to regenerate with fresh AI content.
          </p>
          {Object.keys(regenSelections).map(key => {
            const title = SECTION_GROUPS.flatMap(g => g.sections).find(s => s.key === key)?.title || key;
            return (
              <Form.Check
                key={key}
                type="checkbox"
                id={`regen-${key}`}
                label={title}
                checked={regenSelections[key] || false}
                onChange={e => setRegenSelections(prev => ({ ...prev, [key]: e.target.checked }))}
                className="mb-2"
              />
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowRegenModal(false)}>Cancel</Button>
          <Button
            onClick={() => {
              // Exclude keys = edited sections the user did NOT check (preserve them)
              const excludeKeys = Object.entries(regenSelections)
                .filter(([, checked]) => !checked)
                .map(([k]) => k);
              executeRegeneration(excludeKeys);
            }}
            style={{ background: 'linear-gradient(90deg, #D03737 0%, #A30202 100%)', border: 'none', color: '#fff' }}
          >
            Regenerate
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Print styles */}
      <style>{`
        @media print {
          /* Hide navigation, chrome, and floating widgets */
          .no-print { display: none !important; }
          aside { display: none !important; }
          header { display: none !important; }
          .feedback-button { display: none !important; }

          /* Make main content full width */
          main {
            margin-left: 0 !important;
            padding-top: 0 !important;
            width: 100% !important;
          }
          .pdf-main-content {
            width: 100% !important;
            flex: 0 0 100% !important;
            max-width: 100% !important;
          }

          /* Show print-only elements */
          .no-screen { display: block !important; }

          /* Cover page: own page, centered */
          .print-only-cover {
            page-break-after: always;
            height: 100vh;
            display: flex !important;
            align-items: center;
            justify-content: center;
            margin: 0;
            padding: 0;
          }

          /* Each section group starts on a new page, except the first one
             (which follows the cover page break) */
          .print-section + .print-section {
            page-break-before: always;
          }

          /* Keep group header glued to the first card */
          .print-group-header {
            page-break-after: avoid;
          }

          /* Never split a card across pages */
          .print-card-section {
            page-break-inside: avoid;
          }

          /* Never split a card itself */
          .card {
            page-break-inside: avoid;
          }

          /* Keep table rows together */
          tr {
            page-break-inside: avoid;
          }
          thead {
            display: table-header-group;
          }

          /* Keep headings with their content */
          h3, h4, h5, h6 {
            page-break-after: avoid;
          }

          /* Force background colors and text to render */
          * {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
          }

          /* Ensure dark card headers render with gold text */
          .card-header {
            background-color: #0a0a0a !important;
            color: #ddb75e !important;
          }
          .card-header h5, .card-header h6 {
            color: #ddb75e !important;
          }

          /* Dark bg + gold text blocks (archetype hero, messaging primary) */
          .print-dark-gold,
          .print-dark-gold * {
            background-color: #0a0a0a !important;
            color: #ddb75e !important;
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
          }

          /* Energy level badges and other colored badges */
          .badge {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
          }

          /* Body reset for print */
          body {
            margin: 0 !important;
            padding: 0 !important;
          }
        }
        @media screen {
          .no-screen { display: none !important; }
        }
      `}</style>
    </div>
  );
}

// ===========================================================================
// Direct Data Section Renderers (from context, not stored in DB)
// ===========================================================================
function renderDirectSection(key, context) {
  if (!context) return null;
  const design = context.design || {};
  const arch = context.archetypes || {};

  switch (key) {
    case 'direct_archetypes':
      return renderArchetypes(arch);
    case 'direct_voice':
      return renderVoiceDescription(design);
    case 'direct_colors':
      return renderColorPalette(design.custom_colors);
    case 'direct_typography':
      return renderTypography(design.selected_fonts);
    case 'direct_logo':
      return renderLogo(design);
    case 'direct_iconography':
      return renderIconography(design.selected_iconography);
    case 'direct_style':
      return renderStyleTexture(design);
    case 'direct_mood':
      return renderMoodBoard(design.mood_board_images);
    default:
      return null;
  }
}

function renderArchetypes(arch) {
  if (!arch.primary) return <EmptyHint text="Complete the Brand Archetype Quiz in your Profit Map." />;
  return (
    <Card className="shadow-sm">
      <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
        <h5 className="mb-0">Brand Archetypes</h5>
      </Card.Header>
      <Card.Body>
        <div className="d-flex gap-3 flex-wrap">
          {['primary', 'secondary', 'tertiary'].map((type) => {
            const a = arch[type];
            if (!a) return null;
            return (
              <div key={type} className={type === 'primary' ? 'print-dark-gold' : ''} style={{
                padding: '12px 20px',
                borderRadius: '8px',
                backgroundColor: type === 'primary' ? colors.greyDarkest : '#f8f9fa',
                color: type === 'primary' ? colors.goldLight : colors.greyDarkest,
                border: type !== 'primary' ? `1px solid ${colors.greyLight}` : 'none',
              }}>
                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.7 }}>{type}</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 600 }}>{a.name}</div>
                <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>Score: {Math.round(a.score)}%</div>
                {a.adjectives?.length > 0 && (
                  <div className="mt-1">
                    {a.adjectives.map((adj, i) => (
                      <Badge key={i} bg="light" text="dark" className="me-1" style={{ fontSize: '0.7rem' }}>{adj}</Badge>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
}

function renderVoiceDescription(design) {
  if (!design.brand_voice_description) return <EmptyHint text="Complete the Brand Voice section in your Profit Map." />;
  return (
    <Card className="shadow-sm">
      <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
        <h5 className="mb-0">Voice Description</h5>
      </Card.Header>
      <Card.Body>
        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{design.brand_voice_description}</div>
      </Card.Body>
    </Card>
  );
}

function renderColorPalette(customColors) {
  if (!customColors) return <EmptyHint text="Complete the Color Palette in your Profit Map." />;

  // Filter to only actual color entries (hex values), skip _name, description, name fields
  const colorEntries = Object.entries(customColors).filter(([key, val]) => {
    if (key.endsWith('_name') || key === 'name' || key === 'description') return false;
    const hex = typeof val === 'object' ? (val.hex || val.value || '') : val;
    return typeof hex === 'string' && hex.startsWith('#');
  });

  return (
    <Card className="shadow-sm">
      <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
        <h5 className="mb-0">Color Palette</h5>
      </Card.Header>
      <Card.Body>
        {customColors.name && <p className="mb-3" style={{ fontStyle: 'italic', color: colors.greyMid }}>{customColors.name}{customColors.description ? ` — ${customColors.description}` : ''}</p>}
        <div className="d-flex gap-4 flex-wrap">
          {colorEntries.map(([role, val]) => {
            const hex = typeof val === 'object' ? (val.hex || val.value || '') : val;
            // Use the friendly _name if available, otherwise humanize the key
            const displayName = customColors[`${role}_name`] || role.replace(/([a-z])(\d)/g, '$1 $2').replace(/^./, c => c.toUpperCase());
            return (
              <div key={role} style={{ textAlign: 'center', width: 90 }}>
                <div
                  style={{
                    width: 80, height: 80, borderRadius: 8,
                    backgroundColor: hex,
                    border: `1px solid ${colors.greyLight}`,
                    cursor: 'pointer',
                  }}
                  onClick={() => navigator.clipboard.writeText(hex)}
                  title="Click to copy hex"
                />
                <div style={{ fontSize: '0.8rem', fontWeight: 600, marginTop: 6 }}>{displayName}</div>
                <div style={{ fontSize: '0.75rem', color: colors.greyMid }}>{hex}</div>
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
}

function renderTypography(fonts) {
  if (!fonts) return <EmptyHint text="Complete Typography selection in your Profit Map." />;
  const heading = fonts.heading_font || fonts.primary || 'Not selected';
  const body = fonts.body_font || fonts.secondary || 'Not selected';
  return (
    <Card className="shadow-sm">
      <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
        <h5 className="mb-0">Typography</h5>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col md={6}>
            <h6>Heading Font</h6>
            <p style={{ fontFamily: heading, fontSize: '1.5rem' }}>{heading}</p>
            <p style={{ fontFamily: heading, fontSize: '1rem' }}>The quick brown fox jumps over the lazy dog</p>
          </Col>
          <Col md={6}>
            <h6>Body Font</h6>
            <p style={{ fontFamily: body, fontSize: '1.5rem' }}>{body}</p>
            <p style={{ fontFamily: body, fontSize: '1rem' }}>The quick brown fox jumps over the lazy dog</p>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

function renderLogo(design) {
  const hasLogos = design.logo_horizontal || design.logo_vertical;
  const directionRaw = design.selected_shapes?.logo_direction;
  if (!hasLogos && !directionRaw) return <EmptyHint text="Upload logos or complete logo direction in your Profit Map." />;

  // logo_direction can be a string OR a deeply nested object
  const renderDirection = () => {
    if (!directionRaw) return null;
    if (typeof directionRaw === 'string') return <p>{directionRaw}</p>;

    // It's an object — extract meaningful fields
    return (
      <div>
        {directionRaw.overview && <p>{safeText(directionRaw.overview)}</p>}
        {directionRaw.shape_language && <p><strong>Shape Language:</strong> {safeText(directionRaw.shape_language)}</p>}
        {directionRaw.typography && <p><strong>Typography:</strong> {safeText(directionRaw.typography)}</p>}
        {directionRaw.color_approach && (
          <div className="mb-2">
            <strong>Color Approach:</strong>
            {typeof directionRaw.color_approach === 'string'
              ? <span> {directionRaw.color_approach}</span>
              : <ul className="mb-0 mt-1">
                  {Object.entries(directionRaw.color_approach).map(([k, v]) => (
                    <li key={k}><strong>{k}:</strong> {safeText(v)}</li>
                  ))}
                </ul>
            }
          </div>
        )}
        {directionRaw.imagery_style && <p><strong>Imagery Style:</strong> {safeText(directionRaw.imagery_style)}</p>}
        {Array.isArray(directionRaw.design_principles) && (
          <div className="mb-2">
            <strong>Design Principles:</strong>
            <ul className="mb-0 mt-1">
              {directionRaw.design_principles.map((p, i) => <li key={i}>{safeText(p)}</li>)}
            </ul>
          </div>
        )}
        {directionRaw.implementation_notes && <p><strong>Implementation Notes:</strong> {safeText(directionRaw.implementation_notes)}</p>}
        {Array.isArray(directionRaw.examples) && (
          <div className="mb-2">
            <strong>Examples:</strong>
            <ul className="mb-0 mt-1">
              {directionRaw.examples.map((ex, i) => <li key={i}>{safeText(ex)}</li>)}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="shadow-sm">
      <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
        <h5 className="mb-0">Logo</h5>
      </Card.Header>
      <Card.Body>
        {hasLogos && (
          <Row className="mb-3">
            {design.logo_horizontal && (
              <Col md={6} className="text-center">
                <p style={{ fontSize: '0.8rem', color: colors.greyMid }}>Horizontal</p>
                <img src={design.logo_horizontal} alt="Horizontal logo" style={{ maxWidth: '100%', maxHeight: 120 }} />
              </Col>
            )}
            {design.logo_vertical && (
              <Col md={6} className="text-center">
                <p style={{ fontSize: '0.8rem', color: colors.greyMid }}>Vertical</p>
                <img src={design.logo_vertical} alt="Vertical logo" style={{ maxWidth: '100%', maxHeight: 120 }} />
              </Col>
            )}
          </Row>
        )}
        {directionRaw && (
          <div>
            <h6>Logo Direction</h6>
            {renderDirection()}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function renderIconography(iconData) {
  if (!iconData) return <EmptyHint text="Upload your brand marker in your Profit Map." />;

  // Support new brand_marker format AND legacy icon_style format
  const brandMarker = iconData.brand_marker;

  if (brandMarker) {
    // New brand marker format
    const positionLabel = (brandMarker.position || 'bottom-right').split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    return (
      <Card className="shadow-sm">
        <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <h5 className="mb-0">Brand Marker</h5>
        </Card.Header>
        <Card.Body>
          <div className="d-flex align-items-center gap-3 mb-3">
            {brandMarker.primary_image && (
              <div style={{ backgroundColor: '#e8e8e8', borderRadius: '8px', padding: '8px', display: 'inline-flex' }}>
                <img src={brandMarker.primary_image} alt="Brand marker" style={{ width: 64, height: 64, objectFit: 'contain' }} />
              </div>
            )}
            <div>
              <p className="mb-1"><strong>Position:</strong> {positionLabel}</p>
              <p className="mb-1"><strong>Size:</strong> {(brandMarker.size || 'medium').charAt(0).toUpperCase() + (brandMarker.size || 'medium').slice(1)}</p>
              <p className="mb-0"><strong>Opacity:</strong> {brandMarker.opacity || 85}%</p>
            </div>
          </div>
          <p className="text-muted mb-0">
            <small>Applied automatically to all newly generated images.</small>
          </p>
        </Card.Body>
      </Card>
    );
  }

  // Legacy iconography format (backward compat)
  const style = iconData.icon_style;
  const styleOverview = typeof style === 'string' ? style : safeText(style?.overview);

  const imageUrls = (iconData.icon_images || [])
    .map(img => typeof img === 'string' ? img : img?.url)
    .filter(url => typeof url === 'string' && url.length > 0);

  return (
    <Card className="shadow-sm">
      <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
        <h5 className="mb-0">Brand Marker</h5>
      </Card.Header>
      <Card.Body>
        {styleOverview && <p><strong>Legacy style:</strong> {safeText(styleOverview)}</p>}
        {imageUrls.length > 0 && (
          <div className="d-flex gap-2 flex-wrap">
            {imageUrls.map((url, i) => (
              <img key={i} src={url} alt={`Icon ${i + 1}`} style={{ width: 48, height: 48 }} />
            ))}
          </div>
        )}
        <p className="text-muted mt-2 mb-0"><small>Update to the new Brand Marker in your Profit Map for image overlay support.</small></p>
      </Card.Body>
    </Card>
  );
}

function renderStyleTexture(design) {
  const styleWords = design.brand_style?.selected_words || [];
  const textureWords = design.selected_textures?.selected_words || [];
  if (styleWords.length === 0 && textureWords.length === 0) {
    return <EmptyHint text="Complete Style & Texture selections in your Profit Map." />;
  }
  return (
    <Card className="shadow-sm">
      <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
        <h5 className="mb-0">Style & Texture</h5>
      </Card.Header>
      <Card.Body>
        {styleWords.length > 0 && (
          <div className="mb-3">
            <h6>Style Words</h6>
            <div className="d-flex gap-2 flex-wrap">
              {styleWords.map((w, i) => (
                <Badge key={i} style={{ backgroundColor: colors.greyDarkest, fontSize: '0.85rem', padding: '6px 12px' }}>{w}</Badge>
              ))}
            </div>
          </div>
        )}
        {textureWords.length > 0 && (
          <div>
            <h6>Texture Words</h6>
            <div className="d-flex gap-2 flex-wrap">
              {textureWords.map((w, i) => (
                <Badge key={i} bg="light" text="dark" style={{ fontSize: '0.85rem', padding: '6px 12px', border: `1px solid ${colors.greyLight}` }}>{w}</Badge>
              ))}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function renderMoodBoard(moodBoardImages) {
  if (!moodBoardImages?.images?.length) return <EmptyHint text="Generate a Mood Board in your Profit Map." />;
  return (
    <Card className="shadow-sm">
      <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
        <h5 className="mb-0">Mood Board</h5>
      </Card.Header>
      <Card.Body>
        <div className="d-flex gap-3 flex-wrap">
          {moodBoardImages.images.map((img, i) => (
            <img
              key={i}
              src={img.base64 ? `data:image/png;base64,${img.base64}` : img.url || img}
              alt={`Mood board ${i + 1}`}
              style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, border: `1px solid ${colors.greyLight}` }}
            />
          ))}
        </div>
      </Card.Body>
    </Card>
  );
}

// ===========================================================================
// AI Section Content Renderers
// ===========================================================================
function renderSectionContent(key, content) {
  if (!content) return <p className="text-muted">No content</p>;

  const renderers = {
    brand_promise: renderBrandPromise,
    positioning_statement: renderPositioningStatement,
    archetype_rationale: renderArchetypeRationale,
    voice_attributes: renderVoiceAttributes,
    tone_spectrum: renderToneSpectrum,
    writing_dos_donts: renderWritingDosDonts,
    sample_copy_blocks: renderSampleCopyBlocks,
    audience_language_map: renderAudienceLanguageMap,
    decision_drivers: renderDecisionDrivers,
    competitive_position: renderCompetitivePosition,
    messaging_hierarchy: renderMessagingHierarchy,
    semantic_colors: renderSemanticColors,
    dark_mode_colors: renderDarkModeColors,
    imagery_guidelines: renderImageryGuidelines,
    ai_image_prompts: renderAiImagePrompts,
    application_website: renderApplicationWebsite,
    application_email: renderApplicationEmail,
    application_social: renderApplicationSocial,
    application_sales: renderApplicationSales,
    brand_checklist: renderBrandChecklist,
    contrast_ratios: renderContrastRatios,
    type_scale: renderTypeScale,
    spacing_system: renderSpacingSystem,
    layout_grid: renderLayoutGrid,
  };

  const renderer = renderers[key];
  if (renderer) return renderer(content);

  // Fallback: pretty-print JSON
  return <pre style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{JSON.stringify(content, null, 2)}</pre>;
}

function renderBrandPromise(c) {
  return (
    <div>
      <blockquote style={{ borderLeft: `4px solid ${colors.goldMid}`, paddingLeft: 16, fontSize: '1.1rem', fontStyle: 'italic' }}>
        {c.promise}
      </blockquote>
      {c.supporting_statement && <p><strong>Supporting Statement:</strong> {c.supporting_statement}</p>}
      {c.emotional_outcome && <p><strong>Emotional Outcome:</strong> {c.emotional_outcome}</p>}
    </div>
  );
}

function renderPositioningStatement(c) {
  return (
    <div>
      <blockquote style={{ borderLeft: `4px solid ${colors.goldMid}`, paddingLeft: 16, fontSize: '1.05rem', fontStyle: 'italic' }}>
        {c.statement}
      </blockquote>
      <Row className="mt-3">
        {[['For Whom', c.for_whom], ['Category', c.category], ['Key Differentiator', c.key_differentiator], ['Reason to Believe', c.reason_to_believe]].map(([label, val]) => val && (
          <Col md={6} key={label} className="mb-2">
            <strong>{label}:</strong> {val}
          </Col>
        ))}
      </Row>
    </div>
  );
}

function renderArchetypeRationale(c) {
  return (
    <div>
      {[['Primary Rationale', c.primary_rationale], ['Secondary Influence', c.secondary_influence], ['Archetype Blend', c.archetype_blend], ['Customer Perception', c.customer_perception]].map(([label, val]) => val && (
        <div key={label} className="mb-3">
          <h6>{label}</h6>
          <p>{val}</p>
        </div>
      ))}
    </div>
  );
}

function renderVoiceAttributes(c) {
  const attrs = c.attributes || [];
  return (
    <div className="table-responsive">
      <table className="table table-bordered" style={{ fontSize: '0.9rem' }}>
        <thead style={{ backgroundColor: '#f8f9fa' }}>
          <tr>
            <th style={{ width: '25%' }}>Attribute</th>
            <th style={{ width: '37.5%' }}>Means</th>
            <th style={{ width: '37.5%' }}>Does NOT Mean</th>
          </tr>
        </thead>
        <tbody>
          {attrs.map((a, i) => (
            <tr key={i}>
              <td><strong>{a.attribute}</strong></td>
              <td>{a.means}</td>
              <td style={{ color: colors.greyMid }}>{a.does_not_mean}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderToneSpectrum(c) {
  const contexts = c.contexts || [];
  return (
    <Row>
      {contexts.map((ctx, i) => (
        <Col md={6} lg={4} key={i} className="mb-3">
          <Card style={{ border: `1px solid ${colors.greyLight}`, height: '100%' }}>
            <Card.Body>
              <h6 style={{ color: colors.goldDark }}>{ctx.context}</h6>
              <p style={{ fontSize: '0.85rem' }}>{ctx.tone_shift}</p>
              <div style={{ fontSize: '0.85rem', fontStyle: 'italic', color: colors.greyMid, borderLeft: `3px solid ${colors.goldMid}`, paddingLeft: 8 }}>
                "{ctx.example_phrase}"
              </div>
              <Badge className="mt-2" style={{ backgroundColor: ctx.energy_level === 'High' ? '#dc3545' : ctx.energy_level === 'Medium' ? colors.goldMid : colors.navy, fontSize: '0.7rem' }}>
                {ctx.energy_level} Energy
              </Badge>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

function renderWritingDosDonts(c) {
  return (
    <Row>
      <Col md={6}>
        <h6 style={{ color: '#28a745' }}>Do</h6>
        {(c.dos || []).map((d, i) => (
          <div key={i} className="mb-2" style={{ fontSize: '0.9rem' }}>
            <div><strong>{d.rule}</strong></div>
            <div style={{ color: colors.greyMid, fontStyle: 'italic' }}>e.g., "{d.example}"</div>
          </div>
        ))}
      </Col>
      <Col md={6}>
        <h6 style={{ color: '#dc3545' }}>Don't</h6>
        {(c.donts || []).map((d, i) => (
          <div key={i} className="mb-2" style={{ fontSize: '0.9rem' }}>
            <div><strong>{d.rule}</strong></div>
            <div style={{ color: colors.greyMid, fontStyle: 'italic' }}>e.g., "{d.example}"</div>
          </div>
        ))}
      </Col>
    </Row>
  );
}

function renderSampleCopyBlocks(c) {
  return (
    <Row>
      {(c.blocks || []).map((b, i) => (
        <Col md={6} key={i} className="mb-3">
          <Card style={{ border: `1px solid ${colors.greyLight}`, height: '100%' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <Badge style={{ backgroundColor: colors.greyDarkest, fontSize: '0.7rem' }}>{b.type}</Badge>
                <Button
                  variant="link"
                  size="sm"
                  className="no-print"
                  onClick={() => navigator.clipboard.writeText(b.copy)}
                  style={{ color: colors.goldDark, padding: 0, fontSize: '0.75rem' }}
                >
                  Copy
                </Button>
              </div>
              <p className="mt-2 mb-1" style={{ fontSize: '0.95rem' }}>{b.copy}</p>
              {b.notes && <p style={{ fontSize: '0.8rem', color: colors.greyMid }}>{b.notes}</p>}
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

function renderAudienceLanguageMap(c) {
  return (
    <Row>
      {[
        ['Phrases They Use', c.phrases_they_use, colors.navy],
        ['Phrases That Resonate', c.phrases_that_resonate, colors.goldDark],
        ['Phrases to Avoid', c.phrases_to_avoid, '#dc3545'],
        ['Emotional Triggers', c.emotional_triggers, colors.greyDarkest],
      ].map(([label, items, color]) => items?.length > 0 && (
        <Col md={6} key={label} className="mb-3">
          <h6 style={{ color }}>{label}</h6>
          <div className="d-flex gap-1 flex-wrap">
            {items.map((item, i) => (
              <Badge key={i} bg="light" text="dark" style={{ fontSize: '0.8rem', border: `1px solid ${color}40` }}>{item}</Badge>
            ))}
          </div>
        </Col>
      ))}
    </Row>
  );
}

function renderDecisionDrivers(c) {
  return (
    <div>
      <h6>Primary Decision Drivers</h6>
      <div className="table-responsive mb-3">
        <table className="table table-bordered" style={{ fontSize: '0.9rem' }}>
          <thead style={{ backgroundColor: '#f8f9fa' }}>
            <tr><th>Driver</th><th>Weight</th><th>Messaging Approach</th></tr>
          </thead>
          <tbody>
            {(c.primary_drivers || []).map((d, i) => (
              <tr key={i}>
                <td><strong>{d.driver}</strong></td>
                <td><Badge style={{ backgroundColor: d.weight === 'High' ? '#dc3545' : d.weight === 'Medium' ? colors.goldMid : colors.navy }}>{d.weight}</Badge></td>
                <td>{d.messaging_approach}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h6>Common Objections & Responses</h6>
      {(c.objections || []).map((o, i) => (
        <div key={i} className="mb-2" style={{ fontSize: '0.9rem' }}>
          <strong>Objection:</strong> {o.objection}<br />
          <strong>Response:</strong> {o.response_framework}
        </div>
      ))}
    </div>
  );
}

function renderCompetitivePosition(c) {
  return (
    <div>
      {c.market_position && <p><strong>Market Position:</strong> {c.market_position}</p>}
      {(c.differentiators || []).length > 0 && (
        <div className="table-responsive mb-3">
          <table className="table table-bordered" style={{ fontSize: '0.9rem' }}>
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr><th>Area</th><th>Our Approach</th><th>Typical Competitor</th></tr>
            </thead>
            <tbody>
              {c.differentiators.map((d, i) => (
                <tr key={i}><td><strong>{d.area}</strong></td><td>{d.our_approach}</td><td style={{ color: colors.greyMid }}>{d.typical_competitor}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {c.white_space && <p><strong>White Space Opportunity:</strong> {c.white_space}</p>}
    </div>
  );
}

function renderMessagingHierarchy(c) {
  return (
    <div>
      {c.primary_message && (
        <div className="text-center mb-4 p-3 print-dark-gold" style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, borderRadius: 8 }}>
          <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.7 }}>Primary Message</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 600 }}>{c.primary_message}</div>
        </div>
      )}
      {(c.supporting_messages || []).map((sm, i) => (
        <div key={i} className="mb-3">
          <h6>{sm.message}</h6>
          <ul>
            {(sm.proof_points || []).map((pp, j) => <li key={j} style={{ fontSize: '0.9rem' }}>{pp}</li>)}
          </ul>
        </div>
      ))}
      {c.elevator_pitch && (
        <div className="p-3 mt-3" style={{ backgroundColor: '#f8f4ed', borderRadius: 8, borderLeft: `4px solid ${colors.goldMid}` }}>
          <strong>30-Second Elevator Pitch:</strong>
          <p className="mb-0 mt-1">{c.elevator_pitch}</p>
        </div>
      )}
    </div>
  );
}

function renderSemanticColors(c) {
  const roles = c.roles || [];
  return (
    <div className="d-flex gap-3 flex-wrap">
      {roles.map((r, i) => (
        <div key={i} style={{ textAlign: 'center', width: 100 }}>
          <div style={{
            width: 60, height: 60, borderRadius: 8, margin: '0 auto',
            backgroundColor: r.hex, border: `1px solid ${colors.greyLight}`,
            cursor: 'pointer',
          }}
            onClick={() => navigator.clipboard.writeText(r.hex)}
            title={`Click to copy ${r.hex}`}
          />
          <div style={{ fontSize: '0.75rem', fontWeight: 600, marginTop: 4 }}>{r.role}</div>
          <div style={{ fontSize: '0.7rem', color: colors.greyMid }}>{r.hex}</div>
          <div style={{ fontSize: '0.65rem', color: colors.greyMid }}>{r.usage}</div>
        </div>
      ))}
    </div>
  );
}

function renderDarkModeColors(c) {
  const roles = c.roles || [];
  return (
    <div className="table-responsive">
      <table className="table table-bordered" style={{ fontSize: '0.9rem' }}>
        <thead style={{ backgroundColor: '#f8f9fa' }}>
          <tr><th>Role</th><th>Light Mode</th><th>Dark Mode</th><th>Notes</th></tr>
        </thead>
        <tbody>
          {roles.map((r, i) => (
            <tr key={i}>
              <td><strong>{r.role}</strong></td>
              <td>
                <span style={{ display: 'inline-block', width: 16, height: 16, backgroundColor: r.light_hex, border: `1px solid ${colors.greyLight}`, borderRadius: 3, marginRight: 6, verticalAlign: 'middle' }} />
                {r.light_hex}
              </td>
              <td>
                <span style={{ display: 'inline-block', width: 16, height: 16, backgroundColor: r.dark_hex, border: `1px solid ${colors.greyLight}`, borderRadius: 3, marginRight: 6, verticalAlign: 'middle' }} />
                {r.dark_hex}
              </td>
              <td style={{ color: colors.greyMid }}>{r.notes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderContrastRatios(c) {
  const pairs = c.pairs || [];
  return (
    <div className="table-responsive">
      <table className="table table-bordered" style={{ fontSize: '0.85rem' }}>
        <thead style={{ backgroundColor: '#f8f9fa' }}>
          <tr><th>Color 1</th><th>Color 2</th><th>Ratio</th><th>AA Normal</th><th>AA Large</th><th>AAA</th></tr>
        </thead>
        <tbody>
          {pairs.map((p, i) => (
            <tr key={i}>
              <td>
                <span style={{ display: 'inline-block', width: 14, height: 14, backgroundColor: p.color1.hex, border: '1px solid #ccc', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />
                {p.color1.role} ({p.color1.hex})
              </td>
              <td>
                <span style={{ display: 'inline-block', width: 14, height: 14, backgroundColor: p.color2.hex, border: '1px solid #ccc', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />
                {p.color2.role} ({p.color2.hex})
              </td>
              <td><strong>{p.ratio}:1</strong></td>
              <td>{p.aa_normal ? '✓' : '✗'}</td>
              <td>{p.aa_large ? '✓' : '✗'}</td>
              <td>{p.aaa_normal ? '✓' : '✗'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderImageryGuidelines(c) {
  return (
    <div>
      {c.photography_style && <p><strong>Photography Style:</strong> {c.photography_style}</p>}
      {c.illustration_style && <p><strong>Illustration Style:</strong> {c.illustration_style}</p>}
      {c.color_treatment && <p><strong>Color Treatment:</strong> {c.color_treatment}</p>}
      {c.composition_notes && <p><strong>Composition:</strong> {c.composition_notes}</p>}
      <Row>
        {c.mood_descriptors?.length > 0 && (
          <Col md={4} className="mb-2">
            <h6>Mood</h6>
            {c.mood_descriptors.map((m, i) => <Badge key={i} bg="light" text="dark" className="me-1 mb-1" style={{ border: `1px solid ${colors.goldMid}` }}>{m}</Badge>)}
          </Col>
        )}
        {c.subjects?.length > 0 && (
          <Col md={4} className="mb-2">
            <h6>Subjects</h6>
            {c.subjects.map((s, i) => <Badge key={i} bg="light" text="dark" className="me-1 mb-1">{s}</Badge>)}
          </Col>
        )}
        {c.avoid?.length > 0 && (
          <Col md={4} className="mb-2">
            <h6>Avoid</h6>
            {c.avoid.map((a, i) => <Badge key={i} bg="light" text="dark" className="me-1 mb-1" style={{ border: '1px solid #dc354540' }}>{a}</Badge>)}
          </Col>
        )}
      </Row>
    </div>
  );
}

function renderAiImagePrompts(c) {
  return (
    <Row>
      {(c.templates || []).map((t, i) => (
        <Col md={6} key={i} className="mb-3">
          <Card style={{ border: `1px solid ${colors.greyLight}`, height: '100%' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <Badge style={{ backgroundColor: colors.navy }}>{t.use_case}</Badge>
                <Button
                  variant="link" size="sm" className="no-print"
                  onClick={() => navigator.clipboard.writeText(t.prompt)}
                  style={{ color: colors.goldDark, padding: 0, fontSize: '0.75rem' }}
                >Copy Prompt</Button>
              </div>
              <p className="mt-2 mb-1" style={{ fontSize: '0.85rem', fontFamily: 'monospace', backgroundColor: '#f8f9fa', padding: 8, borderRadius: 4 }}>
                {t.prompt}
              </p>
              {t.style_modifiers && <p style={{ fontSize: '0.8rem', color: colors.greyMid }}>Style: {t.style_modifiers}</p>}
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

function renderApplicationWebsite(c) {
  return (
    <div>
      {c.header && (
        <div className="mb-3">
          <h6>Header</h6>
          <p><strong>Layout:</strong> {c.header.layout}</p>
          <p><strong>Nav Style:</strong> {c.header.nav_style}</p>
          <p><strong>CTA Placement:</strong> {c.header.cta_placement}</p>
        </div>
      )}
      {c.hero && (
        <div className="mb-3">
          <h6>Hero Section</h6>
          <p><strong>Headline Formula:</strong> {c.hero.headline_formula}</p>
          <p><strong>Subtext Approach:</strong> {c.hero.subtext_approach}</p>
          {c.hero.cta_text_examples?.length > 0 && (
            <p><strong>CTA Examples:</strong> {c.hero.cta_text_examples.join(' | ')}</p>
          )}
        </div>
      )}
      {(c.sections || []).length > 0 && (
        <div className="mb-3">
          <h6>Page Sections</h6>
          {c.sections.map((s, i) => (
            <div key={i} className="mb-2">
              <strong>{s.section_type}:</strong> {s.purpose}<br />
              <span style={{ color: colors.greyMid, fontSize: '0.9rem' }}>{s.content_approach}</span>
            </div>
          ))}
        </div>
      )}
      {c.footer && (
        <div>
          <h6>Footer Elements</h6>
          <ul>{(c.footer.elements || []).map((el, i) => <li key={i}>{el}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

function renderApplicationEmail(c) {
  return (
    <div>
      {c.subject_line_formulas?.length > 0 && (
        <div className="mb-3">
          <h6>Subject Line Formulas</h6>
          <ul>{c.subject_line_formulas.map((f, i) => <li key={i}>{f}</li>)}</ul>
        </div>
      )}
      {c.preview_text_approach && <p><strong>Preview Text:</strong> {c.preview_text_approach}</p>}
      {c.greeting_style && <p><strong>Greeting Style:</strong> {c.greeting_style}</p>}
      {c.body_structure && <p><strong>Body Structure:</strong> {c.body_structure}</p>}
      {c.cta_style && <p><strong>CTA Style:</strong> {c.cta_style}</p>}
      {c.sign_off_options?.length > 0 && <p><strong>Sign-off Options:</strong> {c.sign_off_options.join(' | ')}</p>}
      {c.frequency_recommendation && <p><strong>Frequency:</strong> {c.frequency_recommendation}</p>}
    </div>
  );
}

function renderApplicationSocial(c) {
  return (
    <div>
      {c.overall_approach && <p>{c.overall_approach}</p>}
      {(c.content_pillars || []).length > 0 && (
        <div className="mb-3">
          <h6>Content Pillars</h6>
          <Row>
            {c.content_pillars.map((p, i) => (
              <Col md={6} key={i} className="mb-2">
                <div className="p-2" style={{ border: `1px solid ${colors.greyLight}`, borderRadius: 4 }}>
                  <strong>{p.pillar}</strong> <Badge bg="light" text="dark" style={{ fontSize: '0.7rem' }}>{p.ratio}</Badge>
                  <div style={{ fontSize: '0.85rem', color: colors.greyMid }}>{p.description}</div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      )}
      {(c.platforms || []).length > 0 && (
        <div className="mb-3">
          <h6>Platform Guidelines</h6>
          {c.platforms.map((p, i) => (
            <div key={i} className="mb-2">
              <strong>{p.platform}:</strong> {p.tone_notes}
              {p.content_types?.length > 0 && <div style={{ fontSize: '0.85rem', color: colors.greyMid }}>Content types: {p.content_types.join(', ')}</div>}
              {p.posting_tips && <div style={{ fontSize: '0.85rem', color: colors.greyMid }}>Tips: {p.posting_tips}</div>}
            </div>
          ))}
        </div>
      )}
      {c.hashtag_strategy && <p><strong>Hashtag Strategy:</strong> {c.hashtag_strategy}</p>}
      {c.engagement_style && <p><strong>Engagement Style:</strong> {c.engagement_style}</p>}
    </div>
  );
}

function renderApplicationSales(c) {
  return (
    <div>
      {c.headline_formulas?.length > 0 && (
        <div className="mb-3">
          <h6>Headline Formulas</h6>
          <ul>{c.headline_formulas.map((f, i) => <li key={i}>{f}</li>)}</ul>
        </div>
      )}
      {c.story_framework && <p><strong>Story Framework:</strong> {c.story_framework}</p>}
      {c.proof_elements?.length > 0 && <p><strong>Proof Elements:</strong> {c.proof_elements.join(', ')}</p>}
      {c.urgency_approach && <p><strong>Urgency Approach:</strong> {c.urgency_approach}</p>}
      {c.price_presentation && <p><strong>Price Presentation:</strong> {c.price_presentation}</p>}
      {c.guarantee_style && <p><strong>Guarantee Style:</strong> {c.guarantee_style}</p>}
      {(c.cta_progression || []).length > 0 && (
        <div className="mt-3">
          <h6>CTA Progression</h6>
          <div className="table-responsive">
            <table className="table table-bordered" style={{ fontSize: '0.9rem' }}>
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr><th>Stage</th><th>CTA Text</th><th>Placement</th></tr>
              </thead>
              <tbody>
                {c.cta_progression.map((cp, i) => (
                  <tr key={i}><td>{cp.stage}</td><td><strong>{cp.cta_text}</strong></td><td>{cp.placement}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function renderBrandChecklist(c) {
  return (
    <div>
      {(c.categories || []).map((cat, i) => (
        <div key={i} className="mb-3">
          <h6 style={{ color: colors.goldDark }}>{cat.category}</h6>
          {(cat.items || []).map((item, j) => (
            <div key={j} className="d-flex gap-2 mb-1" style={{ fontSize: '0.9rem' }}>
              <span style={{ opacity: 0.4 }}>&#9744;</span>
              <div>
                <strong>{item.item}</strong>
                {item.description && <span style={{ color: colors.greyMid }}> — {item.description}</span>}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function renderTypeScale(c) {
  return (
    <div>
      <p><strong>Base size:</strong> {c.base_size}px | <strong>Scale ratio:</strong> {c.ratio} ({c.ratio_name}) | <strong>Heading:</strong> {c.heading_font} | <strong>Body:</strong> {c.body_font}</p>
      <div className="table-responsive">
        <table className="table table-bordered" style={{ fontSize: '0.9rem' }}>
          <thead style={{ backgroundColor: '#f8f9fa' }}>
            <tr><th>Token</th><th>Label</th><th>Size (px)</th><th>Size (rem)</th><th>Preview</th></tr>
          </thead>
          <tbody>
            {(c.scale || []).map((s, i) => (
              <tr key={i}>
                <td><code>{s.name}</code></td>
                <td>{s.label}</td>
                <td>{s.px}</td>
                <td>{s.rem}</td>
                <td style={{ fontSize: `${s.px}px`, lineHeight: 1.2, maxHeight: 40, overflow: 'hidden' }}>Aa</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function renderSpacingSystem(c) {
  return (
    <div>
      <p><strong>Base unit:</strong> {c.base}px</p>
      <div className="d-flex gap-3 flex-wrap align-items-end" style={{ padding: '12px 0' }}>
        {(c.scale || []).map((s, i) => {
          // Use a min visible size of 16px for display, but show real value
          const displaySize = Math.max(s.px, 16);
          const cappedSize = Math.min(displaySize, 80);
          return (
            <div key={i} style={{ textAlign: 'center', minWidth: 50 }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: 80 }}>
                <div style={{
                  width: cappedSize, height: cappedSize,
                  backgroundColor: colors.goldMid + '40',
                  border: `2px solid ${colors.goldMid}`,
                  borderRadius: 3,
                }} />
              </div>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, marginTop: 6 }}>{s.token}</div>
              <div style={{ fontSize: '0.7rem', color: colors.greyMid }}>{s.px}px</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function renderLayoutGrid(c) {
  return (
    <div>
      <p><strong>Column system:</strong> {c.columns}-column grid</p>
      <div className="table-responsive">
        <table className="table table-bordered" style={{ fontSize: '0.9rem' }}>
          <thead style={{ backgroundColor: '#f8f9fa' }}>
            <tr><th>Breakpoint</th><th>Min Width</th><th>Max Width</th><th>Columns</th><th>Margin</th><th>Gutter</th></tr>
          </thead>
          <tbody>
            {(c.breakpoints || []).map((bp, i) => (
              <tr key={i}>
                <td><strong>{bp.name}</strong></td>
                <td>{bp.min_width}px</td>
                <td>{bp.max_width ? `${bp.max_width}px` : '∞'}</td>
                <td>{bp.columns}</td>
                <td>{typeof bp.margin === 'number' ? `${bp.margin}px` : bp.margin}</td>
                <td>{bp.gutter}px</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===========================================================================
// Empty state hint
// ===========================================================================
// Safely convert any value to a renderable string — prevents "Objects are not valid as React child"
function safeText(val) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (Array.isArray(val)) return val.map(safeText).join(', ');
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

function EmptyHint({ text }) {
  return (
    <Card className="shadow-sm" style={{ opacity: 0.6 }}>
      <Card.Body className="text-center py-3">
        <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>{text}</p>
      </Card.Body>
    </Card>
  );
}

// ===========================================================================
// Generic edit form for JSON content
// ===========================================================================
function EditForm({ content, onChange }) {
  if (!content || typeof content !== 'object') {
    return (
      <textarea
        className="form-control"
        rows={10}
        value={typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
        onChange={(e) => {
          try { onChange(JSON.parse(e.target.value)); } catch { /* let them keep typing */ }
        }}
      />
    );
  }

  const handleFieldChange = (key, value) => {
    onChange({ ...content, [key]: value });
  };

  const humanLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div>
      {Object.entries(content).map(([key, value]) => {
        const label = humanLabel(key);

        // Simple string field
        if (typeof value === 'string') {
          return (
            <div key={key} className="mb-3">
              <label className="form-label fw-bold">{label}</label>
              <textarea
                className="form-control"
                rows={Math.min(Math.max(value.length / 60, 2), 6)}
                value={value}
                onChange={(e) => handleFieldChange(key, e.target.value)}
              />
            </div>
          );
        }

        // Array of strings — editable list with add/remove
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
          return (
            <div key={key} className="mb-3">
              <label className="form-label fw-bold">{label}</label>
              {value.map((item, i) => (
                <div key={i} className="d-flex gap-2 mb-1">
                  <input
                    className="form-control form-control-sm"
                    value={item}
                    onChange={(e) => {
                      const updated = [...value];
                      updated[i] = e.target.value;
                      handleFieldChange(key, updated);
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => handleFieldChange(key, value.filter((_, j) => j !== i))}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mt-1"
                onClick={() => handleFieldChange(key, [...value, ''])}
              >
                + Add Item
              </button>
            </div>
          );
        }

        // Array of objects — editable cards with labeled fields
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
          return (
            <div key={key} className="mb-3">
              <label className="form-label fw-bold">{label}</label>
              {value.map((item, i) => (
                <div key={i} className="border rounded p-2 mb-2" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <small className="text-muted">Item {i + 1}</small>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm py-0"
                      onClick={() => handleFieldChange(key, value.filter((_, j) => j !== i))}
                    >
                      Remove
                    </button>
                  </div>
                  {Object.entries(item).map(([subKey, subVal]) => (
                    <div key={subKey} className="mb-1">
                      <label className="form-label mb-0" style={{ fontSize: '0.8rem', color: colors.greyMid }}>{humanLabel(subKey)}</label>
                      {typeof subVal === 'string' ? (
                        <textarea
                          className="form-control form-control-sm"
                          rows={Math.min(Math.max(String(subVal).length / 60, 1), 3)}
                          value={subVal}
                          onChange={(e) => {
                            const updated = [...value];
                            updated[i] = { ...item, [subKey]: e.target.value };
                            handleFieldChange(key, updated);
                          }}
                        />
                      ) : Array.isArray(subVal) && subVal.every(v => typeof v === 'string') ? (
                        <div>
                          {subVal.map((sv, si) => (
                            <div key={si} className="d-flex gap-1 mb-1">
                              <input
                                className="form-control form-control-sm"
                                value={sv}
                                onChange={(e) => {
                                  const updatedSub = [...subVal];
                                  updatedSub[si] = e.target.value;
                                  const updated = [...value];
                                  updated[i] = { ...item, [subKey]: updatedSub };
                                  handleFieldChange(key, updated);
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm py-0"
                                onClick={() => {
                                  const updated = [...value];
                                  updated[i] = { ...item, [subKey]: subVal.filter((_, sj) => sj !== si) };
                                  handleFieldChange(key, updated);
                                }}
                              >x</button>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm py-0 mt-1"
                            onClick={() => {
                              const updated = [...value];
                              updated[i] = { ...item, [subKey]: [...subVal, ''] };
                              handleFieldChange(key, updated);
                            }}
                          >+ Add</button>
                        </div>
                      ) : (
                        <input
                          className="form-control form-control-sm"
                          value={safeText(subVal)}
                          onChange={(e) => {
                            const updated = [...value];
                            updated[i] = { ...item, [subKey]: e.target.value };
                            handleFieldChange(key, updated);
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mt-1"
                onClick={() => {
                  // Clone the structure of the first item with empty values
                  const template = {};
                  Object.keys(value[0]).forEach(k => {
                    const v = value[0][k];
                    template[k] = Array.isArray(v) ? [] : '';
                  });
                  handleFieldChange(key, [...value, template]);
                }}
              >
                + Add {label.replace(/s$/, '')}
              </button>
            </div>
          );
        }

        // Plain object — render sub-fields
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          return (
            <div key={key} className="mb-3">
              <label className="form-label fw-bold">{label}</label>
              <div className="border rounded p-2" style={{ backgroundColor: '#f8f9fa' }}>
                {Object.entries(value).map(([subKey, subVal]) => (
                  <div key={subKey} className="mb-2">
                    <label className="form-label mb-0" style={{ fontSize: '0.8rem', color: colors.greyMid }}>{humanLabel(subKey)}</label>
                    {typeof subVal === 'string' ? (
                      <textarea
                        className="form-control form-control-sm"
                        rows={Math.min(Math.max(String(subVal).length / 60, 1), 3)}
                        value={subVal}
                        onChange={(e) => handleFieldChange(key, { ...value, [subKey]: e.target.value })}
                      />
                    ) : Array.isArray(subVal) && subVal.every(v => typeof v === 'string') ? (
                      <div>
                        {subVal.map((sv, si) => (
                          <div key={si} className="d-flex gap-1 mb-1">
                            <input
                              className="form-control form-control-sm"
                              value={sv}
                              onChange={(e) => {
                                const updatedSub = [...subVal];
                                updatedSub[si] = e.target.value;
                                handleFieldChange(key, { ...value, [subKey]: updatedSub });
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm py-0"
                              onClick={() => handleFieldChange(key, { ...value, [subKey]: subVal.filter((_, sj) => sj !== si) })}
                            >x</button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm py-0 mt-1"
                          onClick={() => handleFieldChange(key, { ...value, [subKey]: [...subVal, ''] })}
                        >+ Add</button>
                      </div>
                    ) : (
                      <input
                        className="form-control form-control-sm"
                        value={safeText(subVal)}
                        onChange={(e) => handleFieldChange(key, { ...value, [subKey]: e.target.value })}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

export default BrandGuide;
